import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';

import { cn } from '@/lib/utils';

export type ButtonTabItem<T extends string = string> = {
  value: T;
  label: React.ReactNode;
  icon: React.ComponentType<{ className?: string }>;
  badge?: number | string | null;
  badgeTone?: 'amber' | 'primary' | 'muted';
  hidden?: boolean;
  disabled?: boolean;
};

const ButtonTabs = TabsPrimitive.Root;

const ButtonTabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, children, ...props }, ref) => (
  <div className="w-full">
    <TabsPrimitive.List
      ref={ref}
      className={cn(
        'flex w-full flex-nowrap items-stretch gap-3 overflow-visible px-0.5 pb-1 pt-3',
        className
      )}
      {...props}
    >
      {children}
    </TabsPrimitive.List>
  </div>
));
ButtonTabsList.displayName = 'ButtonTabsList';

const ButtonTabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {
    icon: React.ComponentType<{ className?: string }>;
    badge?: number | string | null;
    badgeTone?: 'amber' | 'primary' | 'muted';
  }
>(({ className, icon: Icon, badge, badgeTone = 'amber', children, ...props }, ref) => {
  const showBadge = badge !== null && badge !== undefined && badge !== '';

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        'relative inline-flex w-[4.5rem] shrink-0 flex-col items-center justify-center gap-1.5 rounded-xl border px-1 py-2.5 text-center transition-all duration-200',
        'border-border bg-card text-muted-foreground shadow-sm',
        'hover:border-primary/40 hover:text-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50',
        'data-[state=active]:border-primary data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md data-[state=active]:shadow-primary/25',
        className
      )}
      {...props}
    >
      {showBadge ? (
        <span
          className={cn(
            'absolute -right-2 -top-2 z-10 flex h-7 min-w-7 items-center justify-center rounded-full border-2 border-background px-1 text-xs font-black leading-none shadow-md',
            badgeTone === 'primary' && 'bg-primary text-primary-foreground',
            badgeTone === 'muted' && 'bg-muted text-foreground',
            badgeTone === 'amber' && 'bg-yellow-300 text-yellow-900'
          )}
        >
          {badge}
        </span>
      ) : null}
      <Icon className="h-7 w-7 shrink-0 stroke-[2]" />
      <span className="w-full truncate text-[10px] font-semibold leading-tight tracking-tight sm:text-[11px]">
        {children}
      </span>
    </TabsPrimitive.Trigger>
  );
});
ButtonTabsTrigger.displayName = 'ButtonTabsTrigger';

const ButtonTabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-4 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className
    )}
    {...props}
  />
));
ButtonTabsContent.displayName = 'ButtonTabsContent';

type ButtonTabBarProps<T extends string> = Omit<
  React.ComponentPropsWithoutRef<typeof ButtonTabs>,
  'onValueChange'
> & {
  items: ButtonTabItem<T>[];
  listClassName?: string;
  triggerClassName?: string;
  onValueChange?: (value: T) => void;
};

function ButtonTabBar<T extends string>({
  items,
  listClassName,
  triggerClassName,
  onValueChange,
  children,
  ...tabsProps
}: ButtonTabBarProps<T>) {
  const visibleItems = items.filter((item) => !item.hidden);

  return (
    <ButtonTabs
      {...tabsProps}
      onValueChange={onValueChange ? (value) => onValueChange(value as T) : undefined}
    >
      <ButtonTabsList className={listClassName}>
        {visibleItems.map((item) => (
          <ButtonTabsTrigger
            key={item.value}
            value={item.value}
            icon={item.icon}
            badge={item.badge}
            badgeTone={item.badgeTone}
            disabled={item.disabled}
            className={triggerClassName}
          >
            {item.label}
          </ButtonTabsTrigger>
        ))}
      </ButtonTabsList>
      {children}
    </ButtonTabs>
  );
}

export { ButtonTabs, ButtonTabsList, ButtonTabsTrigger, ButtonTabsContent, ButtonTabBar };
